import { useEffect, useRef, useState } from "react";
import { AlertTriangle, Check, ShieldAlert, Lock, ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";

const AgencyTrap = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section ref={sectionRef} className="py-12 md:py-20 relative overflow-hidden bg-card">
      {/* Background Elements */}
      <div className="absolute inset-0 bg-gradient-to-b from-background via-transparent to-background" />
      <div className="absolute top-0 left-0 w-96 h-96 bg-destructive/10 rounded-full blur-3xl -translate-x-1/2" />
      <div className="absolute bottom-0 right-0 w-96 h-96 bg-primary/10 rounded-full blur-3xl translate-x-1/2" />

      <div className="container mx-auto px-6 relative">
        {/* Section Header */}
        <div className="text-center mb-6 md:mb-10">
          <div
            className={`inline-flex items-center gap-2 px-3 py-1.5 md:px-4 md:py-2 bg-destructive/10 border border-destructive/30 rounded-full mb-4 md:mb-6 transition-all duration-700 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            <AlertTriangle size={16} className="text-destructive md:w-[18px] md:h-[18px]" />
            <span className="text-xs md:text-sm font-bold text-destructive uppercase tracking-wider">
              Industry Secret Exposed
            </span>
          </div>

          <h2
            className={`text-2xl md:text-5xl lg:text-6xl font-black mb-4 md:mb-6 ${
              isVisible ? "animate-cinematic-fade-up" : "opacity-0"
            }`}
            style={{ animationDelay: "100ms", animationFillMode: "forwards" }}
          >
            Why I Am the <span className="text-destructive">Smart Alternative</span> to NY Agencies
          </h2>

          <p
            className={`text-xs md:text-xl text-muted-foreground max-w-3xl mx-auto transition-all duration-700 delay-200 ${
              isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
            }`}
          >
            Many agencies lure you in with a "cheap" or "free" build, only to lock you into a contract 
            costing <span className="text-foreground font-semibold">$200 to $1,000+ per month</span>. 
            I offer <span className="text-foreground font-semibold">flat rate pricing</span> with <span className="text-foreground font-semibold">no hidden fees</span> and <span className="text-foreground font-semibold">direct support</span>.
          </p>
        </div>

        {/* The Math */}
        <div
          className={`max-w-4xl mx-auto mb-8 md:mb-16 transition-all duration-700 delay-300 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-16"
          }`}
        >
          <div className="grid grid-cols-2 gap-3 md:gap-6">
            {/* The Trap Card */}
            <div className="relative p-3 md:p-8 border-2 border-destructive/40 bg-gradient-to-br from-destructive/10 via-card to-destructive/5 rounded-xl transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-[0_20px_40px_-15px_hsl(var(--destructive)/0.3)]">
              <div className="absolute -top-2 md:-top-3 left-3 md:left-6">
                <span className="px-2 py-0.5 md:px-3 md:py-1 bg-destructive text-destructive-foreground text-[10px] md:text-xs font-bold uppercase rounded-full">
                  Their Model
                </span>
              </div>

              <div className="flex items-center gap-1.5 md:gap-3 mb-2 md:mb-4 mt-2">
                <ShieldAlert className="text-destructive w-4 h-4 md:w-7 md:h-7" />
                <h3 className="text-xs md:text-xl font-black text-destructive">The Hidden Cost</h3>
              </div>

              <div className="space-y-1.5 md:space-y-4">
                <div className="flex items-center justify-between p-1.5 md:p-3 bg-background/50 rounded-lg border border-destructive/20">
                  <span className="text-[10px] md:text-base text-muted-foreground">"Free" Build</span>
                  <span className="text-[10px] md:text-base font-bold text-foreground">$0</span>
                </div>
                <div className="flex items-center justify-between p-1.5 md:p-3 bg-background/50 rounded-lg border border-destructive/20">
                  <span className="text-[10px] md:text-base text-muted-foreground">Monthly Lock-in</span>
                  <span className="text-[10px] md:text-base font-bold text-destructive">$200+/mo</span>
                </div>
                <div className="h-px bg-destructive/30" />
                <div className="flex items-center justify-between p-1.5 md:p-4 bg-destructive/20 rounded-lg border border-destructive/40">
                  <span className="text-[10px] md:text-base font-bold text-foreground">5-Year Total</span>
                  <span className="text-sm md:text-2xl font-black text-destructive">$12,000+</span>
                </div>
              </div>

              <p className="text-[9px] md:text-sm text-muted-foreground mt-2 md:mt-4 italic">
                * You don't own your site
              </p>
            </div>

            {/* Our Promise Card */}
            <div className="relative p-3 md:p-8 border-2 border-primary/40 bg-gradient-to-br from-primary/10 via-card to-accent/5 rounded-xl transition-all duration-300 ease-out hover:-translate-y-2 hover:shadow-[0_20px_40px_-15px_hsl(var(--primary)/0.3)]">
              <div className="absolute -top-2 md:-top-3 left-3 md:left-6">
                <span className="px-2 py-0.5 md:px-3 md:py-1 bg-primary text-primary-foreground text-[10px] md:text-xs font-bold uppercase rounded-full">
                  My Promise
                </span>
              </div>

              <div className="flex items-center gap-1.5 md:gap-3 mb-2 md:mb-4 mt-2">
                <Lock className="text-primary w-4 h-4 md:w-7 md:h-7" />
                <h3 className="text-xs md:text-xl font-black text-primary">You Own Your Site</h3>
              </div>

              <div className="space-y-1.5 md:space-y-4">
                <div className="flex items-start gap-1.5 md:gap-3 p-1.5 md:p-3 bg-background/50 rounded-lg border border-primary/20">
                  <Check className="text-green-500 flex-shrink-0 mt-0.5 w-3 h-3 md:w-5 md:h-5" />
                  <div>
                    <p className="text-[10px] md:text-base font-semibold text-foreground">No Hostage Fees</p>
                    <p className="text-[9px] md:text-sm text-muted-foreground hidden md:block">You can take your site anywhere, anytime</p>
                  </div>
                </div>
                <div className="flex items-start gap-1.5 md:gap-3 p-1.5 md:p-3 bg-background/50 rounded-lg border border-primary/20">
                  <Check className="text-green-500 flex-shrink-0 mt-0.5 w-3 h-3 md:w-5 md:h-5" />
                  <div>
                    <p className="text-[10px] md:text-base font-semibold text-foreground">No Hidden Contracts</p>
                    <p className="text-[9px] md:text-sm text-muted-foreground hidden md:block">Transparent pricing, no surprise renewals</p>
                  </div>
                </div>
                <div className="flex items-start gap-1.5 md:gap-3 p-1.5 md:p-3 bg-background/50 rounded-lg border border-primary/20">
                  <Check className="text-green-500 flex-shrink-0 mt-0.5 w-3 h-3 md:w-5 md:h-5" />
                  <div>
                    <p className="text-[10px] md:text-base font-semibold text-foreground">Full Ownership</p>
                    <p className="text-[9px] md:text-sm text-muted-foreground hidden md:block">Your code, your domain, your assets—yours forever</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom CTA */}
        <div
          className={`text-center transition-all duration-700 delay-500 ${
            isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"
          }`}
        >
          <p className="text-sm md:text-xl font-medium text-foreground mb-3 md:mb-4">
            At Wave & Wire, you own your site. <span className="text-primary font-bold">Period.</span>
          </p>
          <Link
            to="/services-pricing"
            className="inline-block px-5 py-2.5 md:px-8 md:py-4 bg-primary text-primary-foreground font-bold text-xs md:text-sm uppercase tracking-wider transition-all duration-300 hover:scale-105 hover:shadow-[0_0_30px_hsl(var(--primary)/0.4)]"
          >
            See Transparent Pricing
          </Link>
          
          {/* Read More Link */}
          <div className="mt-4">
            <Link
              to="/transparent-pricing"
              className="inline-flex items-center gap-2 text-primary font-semibold hover:underline transition-all duration-300 group text-sm md:text-base"
            >
              <span>Learn More About My Pricing Philosophy</span>
              <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AgencyTrap;
